import React, { useState } from 'react';
import {
  Trash2, Shield, AlertTriangle, Loader2, CheckCircle, Search
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDataCleanup = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setResult(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/cleanup/preview`, { withCredentials: true });
      setPreview(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to preview cleanup');
    }
    setLoading(false);
  };

  const executeCleanup = async () => {
    if (!window.confirm('Are you sure? This will permanently delete all test/example records. Real data will be kept.')) return;
    setCleaning(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/cleanup/execute`,
        { confirm: 'DELETE_TEST_DATA' }, { withCredentials: true });
      setResult(res.data);
      setPreview(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Cleanup failed');
    }
    setCleaning(false);
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid="data-cleanup-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
          <Trash2 className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">Clear Test / Sample Data</h3>
          <p className="text-[10px] text-[#4B5563]">Remove all demo, test, and example records. Keeps real donations and attendees.</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-amber-800">
          <strong>What gets removed:</strong> Records with emails ending in <code>@example.com</code>, <code>@test.com</code>, <code>@scanner.com</code>, or names starting with <code>TEST_</code>.
          <br /><strong>What stays:</strong> All real donations, real attendees, and real emails (gmail.com, etc.)
        </div>
      </div>

      {/* Step 1: Preview */}
      {!preview && !result && (
        <button onClick={runPreview} disabled={loading}
          className="text-xs font-semibold px-4 py-2 rounded-lg border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1.5 disabled:opacity-50"
          data-testid="cleanup-preview-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {loading ? 'Scanning...' : 'Scan for Test Data'}
        </button>
      )}

      {/* Step 2: Show preview */}
      {preview && (
        <div className="space-y-4" data-testid="cleanup-preview">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{preview.test_donations.count}</p>
              <p className="text-[10px] text-red-700">Test Donations</p>
              <p className="text-[10px] text-red-500">${preview.test_donations.total_amount.toFixed(2)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{preview.test_attendees.count}</p>
              <p className="text-[10px] text-red-700">Test Attendees</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{preview.real_donations_kept}</p>
              <p className="text-[10px] text-green-700">Real Donations Kept</p>
            </div>
          </div>

          {/* Test donations list */}
          {preview.test_donations.records.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-red-700 mb-2">Test donations to remove:</p>
              <div className="max-h-40 overflow-y-auto border border-red-100 rounded-lg divide-y divide-red-50">
                {preview.test_donations.records.map((d, i) => (
                  <div key={i} className="px-3 py-2 flex items-center justify-between text-xs">
                    <span className="text-[#0A0A0A] font-medium">{d.name}</span>
                    <span className="text-[#9CA3AF]">{d.email}</span>
                    <span className="text-red-600 font-semibold">${d.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button onClick={executeCleanup} disabled={cleaning}
              className="bg-red-600 text-white text-xs font-bold px-4 py-2.5 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="cleanup-execute-btn">
              {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {cleaning ? 'Cleaning...' : `Delete ${preview.test_donations.count + preview.test_attendees.count} Test Records`}
            </button>
            <button onClick={() => setPreview(null)}
              className="text-xs text-[#4B5563] hover:underline">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="cleanup-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-bold text-green-800">Cleanup Complete</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
            <div><span className="text-[#4B5563]">Donations removed:</span> <strong className="text-red-600">{result.donations_deleted}</strong></div>
            <div><span className="text-[#4B5563]">Attendees removed:</span> <strong className="text-red-600">{result.attendees_deleted}</strong></div>
            <div><span className="text-[#4B5563]">Candles removed:</span> <strong className="text-red-600">{result.candles_deleted}</strong></div>
            <div><span className="text-[#4B5563]">Commitments removed:</span> <strong className="text-red-600">{result.item_commitments_deleted}</strong></div>
          </div>
          <div className="border-t border-green-200 pt-3 text-xs">
            <div className="flex items-center gap-1 mb-1">
              <Shield className="w-3.5 h-3.5 text-green-600" />
              <span className="font-semibold text-green-800">Real data remaining:</span>
            </div>
            <div><span className="text-[#4B5563]">Real donations:</span> <strong>{result.real_donations_remaining}</strong> (${result.real_total_amount.toFixed(2)})</div>
            <div><span className="text-[#4B5563]">Real attendees:</span> <strong>{result.real_attendees_remaining}</strong></div>
          </div>
          <button onClick={() => { setResult(null); window.location.reload(); }}
            className="mt-3 text-xs text-[#BA1826] font-semibold hover:underline">
            Refresh Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDataCleanup;
